import React, { useState } from 'react'
import { MdLock } from 'react-icons/md'
import ValidationSummary from '@/components/common/ValidationSummary'
import PasswordStrength from '@/components/auth/login/PasswordStrength'

/**
 * Step 3 — choose a password (FEDEV-528).
 *
 * Reached only once the code has been confirmed, so nothing on this screen
 * can fail for a reason to do with the code. Splitting the two means a
 * customer retrying a code no longer has to retype their password, and an
 * error here can only ever be about the password itself.
 */
interface Props {
  loading: boolean
  apiError: string
  fieldErrors: Record<string, string[]>
  onSubmit: (password: string, passwordConfirmation: string) => void
  onBack: () => void
}

function LoginSetPasswordStep({
  loading,
  apiError,
  fieldErrors,
  onSubmit,
  onBack,
}: Props): React.JSX.Element {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [strongEnough, setStrongEnough] = useState(false)
  const [error, setError] = useState('')

  const passwordsMatch = confirm.length > 0 && password === confirm
  const canSubmit = strongEnough && passwordsMatch && !loading

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!strongEnough) {
      setError('Please choose a stronger password.')
      return
    }

    if (!passwordsMatch) {
      setError('The two passwords do not match.')
      return
    }

    onSubmit(password, confirm)
  }

  return (
    <div className="login-step login-step--accent">
      <div className="login-step-icon login-step-icon--success">
        <MdLock size={48} />
      </div>
      <h1 className="login-step-heading">Choose a password</h1>
      <p className="login-step-subheading">
        Now that we&rsquo;ve confirmed it&rsquo;s you, choose a password to use
        each time you sign in.
      </p>

      <ValidationSummary errors={fieldErrors} message={apiError} />

      <form onSubmit={submit} className="auth-form" noValidate>
        <div className="form-group">
          <label htmlFor="password" className="form-label">
            Choose a password
          </label>
          <div className="password-wrapper">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              className="form-control form-control-lg"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              disabled={loading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((p) => !p)}
              tabIndex={-1}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <PasswordStrength password={password} onValidityChange={setStrongEnough} />
        </div>

        <div className="form-group">
          <label htmlFor="passwordConfirmation" className="form-label">
            Confirm password
          </label>
          <input
            id="passwordConfirmation"
            type={showPassword ? 'text' : 'password'}
            className="form-control form-control-lg"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Type it again"
            disabled={loading}
          />
          {confirm.length > 0 && !passwordsMatch && (
            <span className="form-text is-invalid">The two passwords do not match.</span>
          )}
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <button
          type="submit"
          className="btn btn-primary btn-lg btn-block"
          disabled={!canSubmit}
        >
          {loading ? 'Setting up…' : 'Finish and sign in'}
        </button>
      </form>

      <button type="button" className="btn btn-link" onClick={onBack}>
        Back
      </button>
    </div>
  )
}

export default LoginSetPasswordStep
